import { useMemo, useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import { usePreviewLanguage, setPreviewLanguage } from '@/hooks/useLanguagePreview';
import {
  MULTILINGUAL_STORIES,
  LANGUAGES,
  WORKFLOW_STAGES,
  languageLabel,
  regionLabelFromId,
} from '@/lib/multilingual';
import type { LangCode, TranslationStage } from '@/lib/multilingual';

const STAGE_TONE: Record<TranslationStage, string> = {
  'Not started': 'bg-background-200 text-foreground-700 border border-background-300',
  Draft: 'bg-secondary-100 text-secondary-900 border border-secondary-200',
  'In review': 'bg-accent-100 text-accent-900 border border-accent-300',
  Approved: 'bg-primary-100 text-primary-700 border border-primary-200',
  Published: 'bg-foreground-950 text-accent-400 border border-foreground-950',
};

function StageBadge({ stage }: { stage: TranslationStage }) {
  return (
    <span className={`inline-flex items-center font-label font-bold uppercase tracking-wide text-[10px] px-2.5 py-1 rounded-sm whitespace-nowrap ${STAGE_TONE[stage]}`}>
      {stage}
    </span>
  );
}

export default function ArticleTranslationPreview() {
  const previewLang = usePreviewLanguage();
  const [selectedId, setSelectedId] = useState(MULTILINGUAL_STORIES[0].id);
  const [view, setView] = useState<'reader' | 'editorial'>('reader');
  const [stages, setStages] = useState<Record<string, Partial<Record<LangCode, TranslationStage>>>>({});
  const [flash, setFlash] = useState('');

  const story = useMemo(
    () => MULTILINGUAL_STORIES.find((s) => s.id === selectedId) ?? MULTILINGUAL_STORIES[0],
    [selectedId],
  );

  const stageFor = (lang: LangCode): TranslationStage => stages[story.id]?.[lang] ?? story.stages[lang] ?? 'Not started';

  const version = story.versions[previewLang];
  const hasVersion = Boolean(version);

  const setStage = (lang: LangCode, stage: TranslationStage) => {
    setStages((prev) => ({ ...prev, [story.id]: { ...prev[story.id], [lang]: stage } }));
    if (stage === 'Approved') setFlash('Translation marked approved in this preview only — nothing is published.');
    else if (stage === 'In review') setFlash('Sent for editorial review in this preview only — no records were changed.');
    else setFlash('Updated in this preview only.');
    setTimeout(() => setFlash(''), 4000);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">Article translation preview</h2>
        <DemoBadge label="Sample translations — not machine-generated live" />
      </div>

      {/* Story picker */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4">
        <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500 mb-3">Choose a sample article</p>
        <div className="flex flex-wrap gap-2">
          {MULTILINGUAL_STORIES.map((s) => {
            const on = s.id === story.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => { setSelectedId(s.id); setView('reader'); }}
                className={`px-3.5 py-2 rounded-full border font-label font-semibold text-[11px] whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-foreground-950 border-foreground-950 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}
              >
                {s.slug.replace(/-/g, ' ')}
              </button>
            );
          })}
        </div>
      </div>

      {/* View switch */}
      <div className="inline-flex flex-wrap gap-1 bg-background-100 border border-background-200 rounded-full p-1">
        {[{ id: 'reader', label: 'Reader view', icon: 'ri-book-open-line' }, { id: 'editorial', label: 'Editorial review', icon: 'ri-quill-pen-line' }].map((v) => (
          <button
            key={v.id}
            type="button"
            onClick={() => setView(v.id as 'reader' | 'editorial')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-label font-bold uppercase tracking-wider text-[11px] whitespace-nowrap cursor-pointer transition-colors ${view === v.id ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className={v.icon}></i></span>
            {v.label}
          </button>
        ))}
      </div>

      {view === 'reader' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Article */}
          <div className="lg:col-span-8 bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="relative h-52 md:h-64 w-full">
              <img src={story.image} alt={story.slug.replace(/-/g, ' ')} title={story.slug.replace(/-/g, ' ')} className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
              <div className="absolute bottom-3 left-4 right-4 flex flex-wrap items-center gap-2">
                <span className="bg-accent-500 text-foreground-950 font-label font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-sm">{story.category}</span>
                <span className="bg-background-50/90 text-foreground-800 font-label font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-sm">{regionLabelFromId(story.regionId)}</span>
              </div>
            </div>
            <div className="p-4 md:p-6">
              {hasVersion ? (
                <>
                  <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-tight">{version.headline}</h3>
                  <p className="mt-3 text-sm text-foreground-500">By {story.author} · showing the {languageLabel(previewLang)} version</p>
                  <p className="mt-4 text-base text-foreground-800 font-medium border-l-2 border-accent-500 pl-4">{version.summary}</p>
                  <div className="mt-5 space-y-4 text-foreground-700 leading-relaxed">
                    {version.body.split('\n\n').map((para, i) => <p key={i} className="text-sm md:text-base">{para}</p>)}
                  </div>
                </>
              ) : (
                <div className="py-8 text-center">
                  <span className="mx-auto w-12 h-12 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-2xl"><i className="ri-translate-2"></i></span>
                  <p className="mt-4 font-heading font-bold text-xl text-foreground-950">No {languageLabel(previewLang)} version yet</p>
                  <p className="mt-2 text-sm text-foreground-600 max-w-md mx-auto">This story has not been translated into {languageLabel(previewLang)}. In the preview it shows a clear &ldquo;translation pending&rdquo; state instead of a placeholder or an inaccurate auto-translation.</p>
                </div>
              )}
            </div>
          </div>

          {/* Side rail */}
          <div className="lg:col-span-4 space-y-4">
            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500 mb-3">Reading language</p>
              <div className="inline-flex flex-wrap gap-1 bg-background-100 border border-background-200 rounded-full p-1">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setPreviewLanguage(l.code)}
                    className={`px-4 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-[11px] whitespace-nowrap cursor-pointer transition-colors ${previewLang === l.code ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}
                  >
                    {l.native}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-foreground-500">The header selector and this control stay in sync. Switching only changes the sample sections that include a translation.</p>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500 mb-3">Language versions</p>
              <ul className="space-y-3">
                {LANGUAGES.map((l) => (
                  <li key={l.code} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-foreground-800">{l.native}</span>
                    <StageBadge stage={stageFor(l.code)} />
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[11px] text-foreground-500 flex items-start gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-information-line"></i></span>
                A story can be published in one language while another translation is still in review.
              </p>
            </div>
          </div>
        </div>
      )}

      {view === 'editorial' && (
        <EditorialReview story={story} stageFor={stageFor} onSetStage={setStage} />
      )}

      {flash && (
        <p className="bg-secondary-50 border border-secondary-200 text-secondary-900 text-sm rounded-md px-4 py-3 flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-information-line"></i></span>
          {flash}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function EditorialReview({
  story,
  stageFor,
  onSetStage,
}: {
  story: (typeof MULTILINGUAL_STORIES)[number];
  stageFor: (lang: LangCode) => TranslationStage;
  onSetStage: (lang: LangCode, stage: TranslationStage) => void;
}) {
  const en = story.versions.en;
  const es = story.versions.es;
  const target: LangCode = story.originalLang === 'en' ? 'es' : 'en';

  return (
    <div className="space-y-5">
      {/* Workflow strip */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4">
        <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500 mb-3">Multilingual workflow (concept)</p>
        <div className="flex flex-wrap items-center gap-2">
          {WORKFLOW_STAGES.map((s, i) => {
            const reached = i <= (stageFor(target) === 'Not started' ? 1 : stageFor(target) === 'Draft' ? 2 : stageFor(target) === 'In review' ? 3 : 4);
            return (
              <span key={s} className="inline-flex items-center gap-2">
                <span className={`font-label uppercase tracking-wide text-[9px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${reached ? 'bg-primary-100 border-primary-200 text-primary-700' : 'bg-background-100 border-background-200 text-foreground-500'}`}>{s}</span>
                {i < WORKFLOW_STAGES.length - 1 && <span className="w-3 h-3 flex items-center justify-center text-foreground-300"><i className="ri-arrow-right-s-line"></i></span>}
              </span>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Original */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex items-center justify-between gap-2">
            <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500">Original — English</p>
            <StageBadge stage={stageFor('en')} />
          </div>
          {en ? (
            <div className="mt-3">
              <h4 className="font-heading font-bold text-lg text-foreground-950 leading-snug">{en.headline}</h4>
              <p className="mt-2 text-sm text-foreground-600">{en.summary}</p>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed line-clamp-[10]">{en.body}</p>
            </div>
          ) : (
            <p className="mt-3 text-sm text-foreground-500">No English version on file.</p>
          )}
        </div>

        {/* Translation */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex items-center justify-between gap-2">
            <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-500">Translation — Español</p>
            <StageBadge stage={stageFor('es')} />
          </div>
          {es ? (
            <div className="mt-3">
              <h4 className="font-heading font-bold text-lg text-foreground-950 leading-snug">{es.headline}</h4>
              <p className="mt-2 text-sm text-foreground-600">{es.summary}</p>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed line-clamp-[10]">{es.body}</p>
            </div>
          ) : (
            <div className="mt-3 py-6 text-center">
              <span className="mx-auto w-10 h-10 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-xl"><i className="ri-translate-2"></i></span>
              <p className="mt-3 text-sm text-foreground-600">Translation not started yet for this article.</p>
            </div>
          )}
          <p className="mt-3 text-[11px] text-foreground-500 flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-robot-2-line"></i></span>
            {story.translator.es ?? 'No translation request yet.'}
          </p>
        </div>
      </div>

      {/* Reviewer decision */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-4">
        <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-600 mb-3">Translation reviewer decision</p>
        <div className="flex flex-wrap gap-2.5">
          <button type="button" onClick={() => onSetStage('es', 'Approved')} className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line"></i></span>Approve translation
          </button>
          <button type="button" onClick={() => onSetStage('es', 'In review')} className="inline-flex items-center gap-2 border border-background-300 text-foreground-700 hover:border-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-line"></i></span>Request changes
          </button>
          <button type="button" onClick={() => onSetStage('en', 'Published')} className="inline-flex items-center gap-2 border border-background-300 text-foreground-700 hover:border-foreground-700 font-label font-bold uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-global-line"></i></span>Publish English only
          </button>
        </div>
        <p className="mt-3 text-[11px] text-foreground-500 flex items-center gap-1.5">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-lock-line"></i></span>
          These decisions are illustrative only — no translation service runs and nothing is published.
        </p>
      </div>
    </div>
  );
}